import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SDGFlipCard from '../components/SDGFlipCard';
import ContactForm from '../components/ContactForm';
import {
  heroContent,
  aboutFoundation,
  sdgCommitments,
  focusAreasPreview,
  impactStats,
  contactCTA,
  images
} from '../data/mock';

const Home = () => {
  useEffect(() => {
    document.title = 'Building an Ethical Circular Economy | ReCircle Foundation';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'ReCircle Foundation is committed to building an ethical circular economy by supporting Safai Saathis, strengthening waste recovery systems, and driving behaviour change.');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Video Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/assets/video-home-section.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </section>

      {/* About the Foundation */}
      <section className="py-20 bg-brand-offwhite">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <pre className="font-sans whitespace-pre-wrap">
              {`${aboutFoundation.intro}
              
${aboutFoundation.mission}

${aboutFoundation.approach}

${aboutFoundation.parentOrg}`}
              <span className="font-semibold text-brand-blue mt-[]">
                {`\n\n${aboutFoundation.goal}`}
              </span>
            </pre>
          </div>
          <div className="mt-8">
            <Link to="/about">
              <button className="bg-cta-blue text-cta-text px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl">
                Know More
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* SDG Commitments */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Our Commitment to
          </h2>
          <h3 className="text-2xl md:text-3xl font-bold text-brand-blue mb-12 text-center">
            Global Sustainable Development Goals
          </h3>

          <div className="flex flex-wrap justify-center gap-8 max-w-6xl mx-auto">
            {sdgCommitments.map((sdg) => (
              <div key={sdg.number} className="w-64">
                <SDGFlipCard sdg={sdg} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 bg-gradient-to-b from-brand-offwhite to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Areas of Action
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {focusAreasPreview.map((area) => (
              <div
                key={area.title}
                className="bg-[#01298a] rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300"
              >
                <div className="flex justify-center mb-6">
                  <img
                    src={area.icon}
                    alt={area.title}
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{area.title}</h3>
                <p className="text-white/90 leading-relaxed text-sm">{area.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/focus-areas">
              <button className="bg-cta-blue text-cta-text px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl">
                Learn More About Our Work
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 bg-brand-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Driven by a Decade of Experience.
          </h2>

          {/* Featured Stat - Full Width */}
          <div className="mb-8">
            <div className="text-center p-8 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300">
              <div className="text-5xl md:text-6xl font-bold text-brand-yellow mb-4">
                {impactStats[0].number}
              </div>
              <p className="text-white text-lg md:text-xl leading-relaxed">
                {impactStats[0].description}
              </p>
            </div>
          </div>

          {/* Other Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.slice(1).map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
              >
                <div className="text-3xl md:text-4xl font-bold text-brand-yellow mb-2">
                  {stat.number}
                </div>
                <p className="text-white/90 text-sm leading-relaxed">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-8">
            <p className="text-white/80 text-sm leading-relaxed text-center italic md:max-w-[70%] mx-auto">
              *The Impact above reflects the work of our parent company, ReCircle, which has spent the last decade building the foundations of India's circular economy.
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-brand-offwhite">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            {contactCTA.title}
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center">
            {contactCTA.description}
          </p>

          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default Home;
